import React, { useCallback, useEffect, useRef, useState } from 'react'

function formatRequirementProgress(req) {
  if (req.key === 'level') return `${req.current}/${req.required}`
  if (req.key === 'ryous') return `${req.current.toLocaleString()}/${req.required.toLocaleString()} ryous`
  if (req.key === 'missionsCompleted') return `${req.current}/${req.required} misiones`
  return `${req.current}/${req.required}`
}

function RankRequirementsPopover({ rankName, node, anchorRect, onClose, onMouseEnter, onMouseLeave }) {
  if (!anchorRect) return null

  const hasRequirements = Array.isArray(node?.requirements) && node.requirements.length > 0
  const spaceBelow = window.innerHeight - anchorRect.bottom
  const placeAbove = spaceBelow < 160
  const popoverStyle = {
    position: 'fixed',
    top: placeAbove ? anchorRect.top - 8 : anchorRect.bottom + 8,
    left: Math.min(Math.max(12, anchorRect.left + anchorRect.width / 2), window.innerWidth - 12),
    transform: placeAbove ? 'translate(-50%, -100%)' : 'translate(-50%, 0)',
    zIndex: 1200,
  }

  return (
    <div
      className="char-rank-popover"
      style={popoverStyle}
      role="tooltip"
      id={`rank-popover-${rankName.replace(/\s+/g, '-')}`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="char-rank-popover__header">
        <span className="char-rank-popover__title">{rankName}</span>
        {node.eligible ? <span className="char-rank-popover__badge">Listo</span> : null}
        <button type="button" className="char-rank-popover__close" onClick={onClose} aria-label="Cerrar">
          ×
        </button>
      </div>
      {!hasRequirements ? (
        <p className="char-rank-popover__pending">Requisitos próximamente</p>
      ) : (
        <ul className="char-rank-popover__checks">
          {node.requirements.map((req) => (
            <li
              key={req.key}
              className={`char-rank-popover__check${
                req.met ? ' char-rank-popover__check--met' : ' char-rank-popover__check--unmet'
              }`}
            >
              <span className="char-rank-popover__icon" aria-hidden="true">
                {req.met ? '✓' : '✗'}
              </span>
              <span className="char-rank-popover__label">{req.label}</span>
              {!req.met ? (
                <span className="char-rank-popover__progress">({formatRequirementProgress(req)})</span>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default function ShinobiRankTimeline({ rankPath, loading = false }) {
  const [activeRank, setActiveRank] = useState(null)
  const [anchorRect, setAnchorRect] = useState(null)
  const hoverLockRef = useRef(false)
  const closeTimerRef = useRef(null)

  const ladder = rankPath?.ladder || []
  const activeNode = ladder.find((n) => n.name === activeRank)

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
  }, [])

  const closePopover = useCallback(() => {
    clearCloseTimer()
    setActiveRank(null)
    setAnchorRect(null)
    hoverLockRef.current = false
  }, [clearCloseTimer])

  const scheduleClose = useCallback(() => {
    clearCloseTimer()
    closeTimerRef.current = setTimeout(() => {
      if (!hoverLockRef.current) closePopover()
    }, 120)
  }, [clearCloseTimer, closePopover])

  const openForNode = useCallback((node, el) => {
    if (node.state !== 'future' || !el) return
    const rect = el.getBoundingClientRect()
    setActiveRank(node.name)
    setAnchorRect(rect)
  }, [])

  useEffect(() => {
    if (!activeRank) return undefined

    const onKeyDown = (e) => {
      if (e.key === 'Escape') closePopover()
    }
    const onScroll = () => closePopover()

    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('scroll', onScroll, true)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('scroll', onScroll, true)
      clearCloseTimer()
    }
  }, [activeRank, closePopover, clearCloseTimer])

  if (loading) {
    return <p className="char-progresion__empty">Cargando camino del shinobi…</p>
  }

  if (ladder.length === 0) {
    return <p className="char-progresion__empty">No hay rangos definidos.</p>
  }

  return (
    <div className="char-shinobi-timeline">
      <ol className="char-shinobi-timeline__nodes" aria-label="Camino del shinobi">
        {ladder.map((node) => {
          const isFuture = node.state === 'future'
          const isOpen = activeRank === node.name

          return (
            <li
              key={node.name}
              className={`char-shinobi-timeline__node char-shinobi-timeline__node--${node.state}${
                isOpen ? ' char-shinobi-timeline__node--open' : ''
              }${node.eligible ? ' char-shinobi-timeline__node--eligible' : ''}`}
            >
              {isFuture ? (
                <button
                  type="button"
                  className="char-shinobi-timeline__node-btn"
                  aria-describedby={isOpen ? `rank-popover-${node.name.replace(/\s+/g, '-')}` : undefined}
                  aria-expanded={isOpen}
                  onMouseEnter={(e) => {
                    clearCloseTimer()
                    openForNode(node, e.currentTarget)
                  }}
                  onMouseLeave={scheduleClose}
                  onFocus={(e) => openForNode(node, e.currentTarget)}
                  onBlur={scheduleClose}
                  onClick={(e) => {
                    if (isOpen) closePopover()
                    else openForNode(node, e.currentTarget)
                  }}
                >
                  <span className="char-shinobi-timeline__dot" aria-hidden="true" />
                  <span className="char-shinobi-timeline__label">{node.name}</span>
                  {node.eligible ? <span className="char-shinobi-timeline__ready">Listo</span> : null}
                </button>
              ) : (
                <div className="char-shinobi-timeline__node-static">
                  <span className="char-shinobi-timeline__dot" aria-hidden="true" />
                  <span className="char-shinobi-timeline__label">{node.name}</span>
                </div>
              )}
            </li>
          )
        })}
      </ol>

      {activeNode && anchorRect ? (
        <RankRequirementsPopover
          rankName={activeRank}
          node={activeNode}
          anchorRect={anchorRect}
          onClose={closePopover}
          onMouseEnter={() => {
            hoverLockRef.current = true
            clearCloseTimer()
          }}
          onMouseLeave={() => {
            hoverLockRef.current = false
            scheduleClose()
          }}
        />
      ) : null}
    </div>
  )
}
